import { useState, useCallback, useEffect, useRef } from 'react';
import api from '../../../services/api';

const CACHE_KEY = 'tenant_config_cache';
const CACHE_DURATION = 0; // Disable cache during development

/**
 * Story 1.14 - Dashboard tenant config hook.
 * Fetches the tenant options from the /config endpoint, caches in localStorage.
 * Returns activeOptions array, optionConfigs, isOptionActive helper, and loading state.
 */
export const useTenantConfig = (storeId) => {
  const [activeOptions, setActiveOptions] = useState([]);
  const [optionConfigs, setOptionConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const cacheRef = useRef(null);

  const fetchConfig = useCallback(async (forceRefresh = false) => {
    try {
      // Check localStorage cache first
      if (!forceRefresh) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_DURATION) {
            setActiveOptions(data.active_options || []);
            setOptionConfigs(data.option_configs || {});
            cacheRef.current = data;
            setLoading(false);
            return;
          }
        }
      }

      // Build the URL with optional tenant_id query param
      const params = storeId ? `?tenant_id=${storeId}` : '';
      const response = await api.get(`/config${params}`);
      const configData = response.data;

      const options = configData.active_options || [];
      const configs = configData.option_configs || {};

      setActiveOptions(options);
      setOptionConfigs(configs);
      cacheRef.current = { active_options: options, option_configs: configs };

      // Persist to localStorage
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        data: { active_options: options, option_configs: configs },
        timestamp: Date.now(),
      }));
    } catch (error) {
      console.error('[useTenantConfig] Error fetching config:', error);
      // Try to use cached data on error
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data } = JSON.parse(cached);
        setActiveOptions(data.active_options || []);
        setOptionConfigs(data.option_configs || {});
      }
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const isOptionActive = useCallback(
    (slug) => activeOptions.includes(slug),
    [activeOptions]
  );

  return {
    activeOptions,
    optionConfigs,
    isOptionActive,
    loading,
    refresh: () => fetchConfig(true),
  };
};

export default useTenantConfig;
